import { DriveStep } from 'driver.js';

export const dashboardSteps: DriveStep[] = [
  {
    popover: {
      title: 'Dashboard Page',
      description:
        'This is your workspace’s statistics dashboard. It contains an overview of all data in your workspace.'
    }
  },
  {
    element: '#dashboard-cards',
    popover: {
      title: 'Dashboard Cards',
      description: 'These boxes summarize how posts have been rated.'
    }
  },
  {
    element: '#dashboard-platforms',
    popover: {
      title: 'Posts per platform',
      description:
        'The origin of all posts that have been captured in your Evaluations.'
    }
  },
  {
    element: '#dashboard-rated',
    popover: {
      title: 'Rated posts',
      description:
        'How many of the posts that were found in Evaluations were rated by someone in your workspace.'
    }
  },
  {
    element: '#dashboard-sponsored',
    popover: {
      title: 'Sponsored posts',
      description:
        'The percentage of posts in evaluations for which the Creator has used the platform feature to disclose advertising (such as ‘Paid Partnership’, or ‘Contains Paid Promotion’).'
    }
  },
  {
    element: '#dashboard-keywords',
    popover: {
      title: 'Occurrences per keyword',
      description:
        'How often every keyword was found in the Evaluations in your workspace.'
    }
  },
  {
    element: '#dashboard-bttn-filter',
    popover: {
      title: 'Filter',
      description:
        'You can filter these results by keyword, date range, Evaluation, platform and rating to obtain more granular insights into your workspace’s contents.'
    }
  }
];

export const ViolationSteps: DriveStep[] = [
  {
    popover: {
      title: 'Violation Page',
      description:
        'This is the Violations page that lists all posts that you and your colleagues marked as a Violation, a breach of advertising rules.'
    }
  },
  {
    element: '#violation-card:nth-child(1)',
    popover: {
      title: 'Total Card',
      description: 'The number of Violations that you have detected.'
    }
  },
  {
    element: '#violation-card:nth-child(2)',
    popover: {
      title: 'Contacted Card',
      description:
        'The number of posts that were followed up by contacting the creator or the advertiser was contacted.'
    }
  },
  {
    element: '#violation-card:nth-child(3)',
    popover: {
      title: 'Changed Card',
      description:
        'The number of posts that the creator changed or removed after posting.'
    }
  },
  {
    element: '#violation-card:nth-child(4)',
    popover: {
      title: 'Closed Card',
      description:
        'The number of violations of which the Status was changed to Closed by you colleagues.'
    }
  },
  {
    element: '#violation-filter',
    popover: {
      title: 'Filter',
      description:
        'By default, this list is filtered to only show Violations with the Status Open and Other.'
    }
  },
  {
    element: '#violation-updates:nth-child(1)',
    popover: {
      title: 'Violation Updates',
      description:
        'Clicking here takes you to a screen that shows all changes that the creator made to their post.'
    }
  },
  {
    element: '#violation-item',
    popover: {
      title: 'Violation Items',
      description:
        'Here you can see all the posts that were marked as a Violation. Clicking one row shows in which Evaluation the post was found, when it was rated, what the rating was and who rated it.'
    }
  }
];

export const ViolationDetailsSteps: DriveStep[] = [
  {
    popover: {
      title: 'Violation Details',
      description:
        'This page shows all information regarding a single Violation.'
    }
  },
  {
    element: '#detail-creator',
    popover: {
      title: 'Creator, date, platform & sponsorship',
      description:
        'Who published this content and when (including collaborators on Instagram). Where this post was published, whether the creator flagged it as commercial content and whether they added an age restriction.'
    }
  },
  {
    element: '#detail-media',
    popover: {
      title: 'Video/image',
      description: 'The content in question.'
    }
  },
  {
    element: '#violation-detail-changes',
    popover: {
      title: 'Changes',
      description:
        'An overview of the changes that the creator made to the post after they published it on social media.'
    }
  },
  {
    element: '#violation-detail-right-panel',
    popover: {
      title: 'Violation Detail panel',
      description:
        'Review and document all relevant information for a post, including its description visibility, tagged accounts, hashtags, content rating and verification, contact history, current status, and a complete timeline with comments to track actions and decisions.'
    }
  },
  {
    element: '#violation-detail-next',
    popover: {
      title: 'Next',
      description: 'Click here to go to the next post in the Violations list.'
    }
  }
];

export const EvaluationSteps: DriveStep[] = [
  {
    popover: {
      title: 'Evaluation Page',
      description:
        'This is the Evaluations page, where you can search through all of the posts that Influencer Monitor has gathered for you.'
    }
  },
  {
    element: '#evaluation-create',
    popover: {
      title: 'Create Evaluation',
      description: 'Click here to start searching.',
      onNextClick: (_, __, options) => {
        const evaluationCreateItem = document.querySelector(
          '#evaluation-create'
        ) as HTMLElement;
        evaluationCreateItem?.click();
        setTimeout(() => {
          options.driver.moveTo(2);
        }, 150);
      }
    }
  },
  {
    popover: {
      title: 'Step1',
      description:
        'Here, we give the evaluation a name and set some general filters.'
    }
  },
  {
    element: '#evaluation-title',
    popover: {
      title: 'Evaluation title',
      description: 'Give your evaluation a name.',
      onPopoverRender: () => {
        const input = document.querySelector(
          '#evaluation-title'
        ) as HTMLInputElement;

        if (input) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          )?.set;

          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(input, 'Demo driver evaluation');
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      }
    }
  },
  {
    element: '#evaluation-date-range',
    popover: {
      title: 'Start & End dates',
      description:
        'Set a start and end data for your analysis. It’s useful coming back to this setting if you get too many or too few results.'
    }
  },
  {
    element: '#evaluation-ongoing',
    popover: {
      title: 'Ongoing',
      description:
        'You can also set a start date without an end date. In this way, Influencer Monitor will add new posts to this evaluation every day, if they fit your criteria.'
    }
  },
  {
    element: '#evaluation-mediatype',
    popover: {
      title: 'Media type',
      description: 'Look for images, videos, or both.'
    }
  },
  {
    element: '#evaluation-creators',
    popover: {
      title: 'Creators',
      description:
        'Specify which creators and creator groups you want to include in your search. Leaving this field empty selects all influencers added to this workspace.'
    }
  },
  {
    element: '#evaluation-followers-range',
    popover: {
      title: 'Followers Range',
      description:
        'Optionally, refine your sample of creators based on their follower number.'
    }
  },
  {
    element: '#evaluation-platform',
    popover: {
      title: 'Platforms',
      description: 'Select the platform and type of post you’re interested in.',
      onNextClick: (_, __, options) => {
        const evaluationCreateContinue = document.querySelector(
          '#create-evaluation-continue'
        ) as HTMLElement;
        evaluationCreateContinue?.click();
        setTimeout(() => {
          options.driver.moveNext();
        }, 150);
      }
    }
  },
  {
    popover: {
      title: 'Step2',
      description:
        'Now, we can get more specific about what we want to find inside of the content.',
      disableButtons: ['previous']
    }
  },
  {
    element: '#evaluation-sponsorship',
    popover: {
      title: 'Sponsorship',
      description:
        'This allows you to include or exclude posts that the creator has marked as commercial by using for example ‘Includes paid promotion’ on YouTube, or ‘Paid Partnership’ on Instagram.'
    }
  },
  {
    element: '#evaluation-urls',
    popover: {
      title: 'URLs',
      description:
        'You can search for posts that include hyperlinks or URLs and even specify which websites.'
    }
  },
  {
    element: '#evaluation-logo',
    popover: {
      title: 'Logo Detection',
      description:
        'Filter out posts that contain brand logos, or select which specific logos you want to see in the results.'
    }
  },
  {
    element: '#evaluation-tags',
    popover: {
      title: 'Tags',
      description:
        'Look for specific user tags/mentions. If you enter multiple, the resulting posts are those that include any of those.'
    }
  },
  {
    element: '#evaluation-hashtags',
    popover: {
      title: 'Hashtags',
      description:
        'Look for specific hashtags. If you enter multiple, the resulting posts are those that include any of those.',
      onNextClick: (_, __, options) => {
        const evaluationCreateContinue = document.querySelector(
          '#create-evaluation-continue'
        ) as HTMLElement;
        evaluationCreateContinue?.click();
        options.driver.moveNext();
      }
    }
  },
  {
    popover: {
      title: 'Step3',
      description:
        'This is where you enter clever combinations of keywords to find the content you’re looking for.',
      disableButtons: ['previous'],
      onPopoverRender: () => {
        const typeKeyword = (value: string) => {
          const input = document.querySelector(
            '#evaluation-input'
          ) as HTMLInputElement;

          if (input) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            )?.set;

            if (nativeInputValueSetter) {
              nativeInputValueSetter.call(input, value);
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(
                new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })
              );
            }
          }
        };

        const clickKeywordButton = () => {
          const keywordButton = document.querySelector(
            '#evaluation-keyword'
          ) as HTMLElement;

          keywordButton?.click();
        };
        clickKeywordButton();
        setTimeout(() => typeKeyword('demo keyword'), 50);
        setTimeout(clickKeywordButton, 150);
        setTimeout(() => typeKeyword('demo keyword second'), 200);
      }
    }
  },
  {
    element: '#evaluation-keyword',
    popover: {
      title: '+KEYWORD',
      description: 'Add a single keyword to your query.'
    }
  },
  {
    element: '#evaluation-keyword-toggle',
    popover: {
      title: 'Toggle button',
      description:
        'Choose whether to include or exclude this keyword from the results.'
    }
  },
  {
    element: '#evaluation-keyword-connector',
    popover: {
      title: 'OR/AND connector',
      description:
        'The logical operator decides whether the search results include either of the two keywords/groups (Or), or both of them (And).'
    }
  },
  {
    element: '#evaluation-keyword-group',
    popover: {
      title: '+KEYWORD GROUP',
      description: 'You can also enter keyword groups from this workspace.'
    }
  },
  {
    element: '#evaluation-keyword-level',
    popover: {
      title: '+LOGICAL LEVEL',
      description:
        'Logical levels allow you to further detail your search query.',
      onPopoverRender: (_, options) => {
        const typeKeyword = (value: string) => {
          const input = document.querySelector(
            '#evaluation-input'
          ) as HTMLInputElement;

          if (input) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            )?.set;

            if (nativeInputValueSetter) {
              nativeInputValueSetter.call(input, value);
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(
                new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })
              );
            }
          }
        };

        const addLevelButton = document.querySelector(
          '#evaluation-keyword-level'
        ) as HTMLElement;
        addLevelButton?.click();

        setTimeout(() => {
          const expandButton = document.querySelector(
            '#evaluation-keyword-expand'
          ) as HTMLElement;
          expandButton?.click();

          setTimeout(() => {
            const keywordButton = document.querySelector(
              '#evaluation-keyword'
            ) as HTMLElement;
            keywordButton?.click();

            setTimeout(() => {
              typeKeyword('keyword logical level');
              options.driver?.refresh();
            }, 50);
          }, 300);
        }, 50);
      }
    }
  },
  {
    element: '#evaluation-keyword-negate',
    popover: {
      title: 'NOT toggle',
      description: 'You can also exclude an entire logical level/group.'
    }
  },
  {
    element: '#evaluation-operator-toggle',
    popover: {
      title: 'ANY/ALL toggle',
      description:
        'Determine whether the search results should include any of the words in this group, or all of them at the same time.'
    }
  },
  {
    element: '#evaluation-additional-terms',
    popover: {
      title: 'Additional terms',
      description:
        'Words in this field are not used as search criteria. That means that posts will show up, regardless of whether they contain these terms. However, when words from this field do appear in the results, they are highlighted for you so that you can easily verify their presence.'
    }
  },
  {
    element: '#evaluation-view-query',
    popover: {
      title: 'VIEW QUERY',
      description: 'You can also review your search query in plain text.',
      onNextClick: (_, __, options) => {
        const evaluationCreateContinue = document.querySelector(
          '#create-evaluation-continue'
        ) as HTMLElement;
        evaluationCreateContinue?.click();
        options.driver.moveNext();
      }
    }
  },
  {
    popover: {
      title: 'Step4',
      disableButtons: ['previous'],
      description:
        'Influencer Monitor is now searching through your collection of posts. This might take a while!'
    }
  },
  {
    element: '#request-detection-minors',
    popover: {
      title: 'Request Detection of Minors',
      description:
        'Here, you can request a facial recognition analysis, to get an idea of how old the people are that appear in the content that was found.',
      onNextClick: (_, __, options) => {
        const closeButton = document
          .querySelector('[data-testid="CloseIcon"]')
          ?.closest('button') as HTMLElement;

        closeButton?.click();

        setTimeout(() => {
          const evaluationLeave = document.querySelector(
            '#evaluation-leave'
          ) as HTMLElement;

          evaluationLeave?.click();
          options.driver.destroy();
        }, 300);
      }
    }
  }
];

export const EvaluationResultsSteps: DriveStep[] = [
  {
    popover: {
      title: 'Evaluation Results',
      description:
        'This is where you can see all posts that match the criteria you set in the previous steps. Click on any row to see more details about a post.'
    }
  },
  /* {
      element: '#evaluation-detail-edit',
      popover: {
          title: 'Edit button',
          description: 'You can edit the search criteria for this Evaluation here.'
      }
  }, */
  {
    element: '#evaluation-detail-chart',
    popover: {
      title: 'Statistics button',
      description:
        'There is also a statistics dashboard specifically for this Evaluation.'
    }
  },
  {
    element: '.evaluation-detail-platform',
    popover: {
      title: 'Platform column of the first row of the results',
      description:
        'Clicking the Platform column takes you outside of Influencer Monitor, to the social media platform where this content was posted. This does not work for Instagram Stories.'
    }
  },
  {
    element: '.evaluation-detail-creator',
    popover: {
      title: 'Creator column of the first row of the results',
      description:
        'Clicking the Creator column takes you to the page containing all information about this creator.',
      onNextClick: (_, __, options) => {
        const evaluationPost = document.querySelector(
          '.evaluation-post'
        ) as HTMLElement;
        evaluationPost?.click();
        options.driver.moveNext();
      }
    }
  }
];

export const EvaluationDetailsSteps: DriveStep[] = [
  {
    popover: {
      title: 'Evaluation post details',
      description: 'This page shows all information regarding a single post.'
    }
  },
  {
    element: '#detail-creator',
    popover: {
      title: 'Creator, date, platform & sponsorship',
      description:
        'Who published this content and when (including collaborators on Instagram). Where this post was published, whether the creator flagged it as commercial content and whether they added an age restriction.'
    }
  },
  {
    element: '#detail-media',
    popover: {
      title: 'Video/image',
      description: 'The content in question.'
    }
  },
  {
    element: '#evaluation-detail-changes',
    popover: {
      title: 'Changes',
      description:
        'An overview of the changes that the creator made to the post after they published it on social media.'
    }
  },
  {
    element: '#evaluation-detail-right-panel',
    popover: {
      title: 'Evaluation Detail panel',
      description:
        'Review all detected elements of a post such as text, visuals, audio, tags, and keywords and record relevance, content type, compliance, and reasoning. Enter your expert assessment by rating the post, marking it as not relevant if needed, classifying the content type (organic, advertising, or questionable), and evaluating compliance with advertising rules.'
    }
  },
  {
    element: '#evaluation-detail-next',
    popover: {
      title: 'Next',
      description: 'Click here to go to the next post in the evaluation.'
    }
  }
];

export const creatorsSteps: DriveStep[] = [
  {
    popover: {
      title: 'Creators Page',
      description:
        'On this page, you can manage the influencer accounts that Influencer Monitor tracks for you. From the moment a Creator’s Account is added to this list, Influencer Monitor starts gathering content from that point in time onwards. All of this content can then be investigated using Evaluations.'
    }
  },
  {
    element: '#create-creator',
    popover: {
      title: 'Add creator',
      description: 'Add a Creator to the list.'
    }
  },
  {
    element: '#creator-import-list',
    popover: {
      title: 'Import List',
      description: 'Add multiple creators in one go by uploading a csv file.'
    }
  },
  {
    element: '#creator-groups-bttn',
    popover: {
      title: 'Groups button',
      description: 'This button takes you to the list of Creator groups.'
    }
  },
  {
    element: '#creators-count',
    popover: {
      title: 'Creators count',
      description:
        'This is the amount of Creators in your list. One Creator can have multiple Accounts on different social media platforms.',
      side: 'top',
      align: 'end'
    }
  },
  {
    element: '#creators-limit-workspace',
    popover: {
      title: 'Accounts limit',
      description:
        'This shows how far you are in reaching the limit for Accounts in your workspace. If you need to increase that limit, let us know!'
    }
  },
  {
    element: '#creators-filter',
    popover: {
      title: 'Filter',
      description:
        'Influencer Monitor checks whether the Accounts you add are actually accessible on the social media platforms. This filter shows you only the Creators with Accounts that Influencer Monitor was not able to find for any reason.'
    }
  },
  {
    element: '#creators-table',
    popover: {
      title: 'Creators Table',
      description:
        'These are all of the Creators that Influencer Monitor is currently following for you.'
    }
  }
];

export const creatorDetailsteps: DriveStep[] = [
  {
    element: '#creators-merge',
    popover: {
      title: 'Merge button',
      description:
        'If you have duplicated Creators in your workspace, this button allows you to merge them.'
    }
  },
  {
    element: '#creators-edit',
    popover: {
      title: 'Edit button',
      description:
        'Here you can change the name, picture, add and remove Accounts for this creator.'
    }
  },
  {
    element: '#creators-add-group',
    popover: {
      title: 'Add to group ',
      description:
        'You can add this Creator to one or more Creator Groups, to further specify your selection.'
    }
  },
  {
    element: '#creators-accounts-list',
    popover: {
      title: 'The Accounts tiles',
      description:
        'These are the accounts Influencer Monitor is currently following.'
    }
  },
  {
    element: '#creators-posts-list',
    popover: {
      title: 'List of posts',
      description:
        'All of the posts by this Creator that Influencer Monitor has collected.'
    }
  }
];

export const KeywordsGroupSteps: DriveStep[] = [
  {
    popover: {
      title: 'Keywords Groups Page',
      description:
        'This page contains lists of keywords. You can use these lists to create clever search queries to help you find relevant content in the deluge of posts that Influencer Monitor gathers every day, based on the Creators in your workspace. Keyword Groups are the key to creating good Evaluations.'
    }
  },
  {
    element: '#keyword-group-add',
    popover: {
      title: 'Add keyword group',
      description: 'Create a new group of keywords.',
      onNextClick: (_, __, options) => {
        const addKeywordsGroup = document.querySelector(
          '#keyword-group-add'
        ) as HTMLElement;
        addKeywordsGroup?.click();
        setTimeout(() => {
          options.driver.moveTo(2);
        }, 150);
      }
    }
  },
  {
    element: '#keyword-title',
    popover: {
      title: 'Group title box',
      description: 'Give the keyword group a descriptive name.'
    }
  },
  {
    element: '#keyword-add',
    popover: {
      title: 'Add keywords box',
      description:
        'Enter keywords here and click ‘+’ or hit enter. You can enter multiple keywords at once, if they are separated by a comma.',
      onNextClick: (_, __, options) => {
        const cancelKeywordsGroup = document.querySelector(
          '#keyword-cancel'
        ) as HTMLElement;
        cancelKeywordsGroup?.click();
        setTimeout(() => {
          options.driver.moveTo(4);
        }, 150);
      }
    }
  },
  {
    element: '#keyword-group-list',
    popover: {
      title: 'List of keywords group',
      description: 'All of the keyword groups currently in your workspace.'
    }
  }
];
